/**
 * FocusTrap component
 */
import * as React from "react"
import { getAllActionables } from "../ActionableSet/utils"
import usePerceptionTrap from "./usePerceptionTrap"
import useInternalId from "../useInternalId"
import FocusContext from "./FocusContext"
import FocusGuard from "./FocusGuard"
import { getDocument } from "../utils"
import { head, pipe, sort, last } from "ramda"

type DefaultProps = typeof defaultProps
type Props = {
  children: React.ReactNode
} & DefaultProps

const defaultProps = {
  /**
   * Automatically moves focus to the first focusable element within the trap.
   * @default true
   */
  autoFocus: true,
  /**
   * Sets whether focus is managed without altering the DOM tree.
   */
  disabled: false,
  /**
   * The underlying DOM element.
   * @default "div"
   */
  from: "div" as keyof JSX.IntrinsicElements | React.ComponentType<any>,
  /**
   * Automatically restores focus to element that was last focused before focus moved
   * within the trap.
   * @default true
   */
  restoreFocus: true as boolean | FocusOptions
}

const getContainedActionables = (element: HTMLElement) => {
  // sort by highest tabIndex value
  const actionables = getAllActionables(element)
  return actionables.length ? actionables : [element]
}
const sortByTabIndex = <T extends HTMLElement>(a: T, b: T) => {
  const idxA = parseFloat(a.getAttribute("tabIndex") ?? "0")
  const idxB = parseFloat(b.getAttribute("tabIndex") ?? "0")
  return idxB - idxA
}
const setFocusTo = (element: HTMLElement | HTMLIFrameElement) => {
  element.focus({ preventScroll: true })
}

const setFocusWithin = pipe(
  getContainedActionables,
  sort(sortByTabIndex),
  head,
  setFocusTo
)
const focusOnStart = pipe(getContainedActionables, head, setFocusTo)
const focusOnEnd = pipe(getContainedActionables, last, setFocusTo)

// is focus already contained
const hasFocus = (elements: HTMLElement[], target: Element | null) => {
  if (elements.length === 0) return false
  const activeElement = target ?? getDocument(elements[0]).activeElement
  return elements.some(el => el === activeElement || el.contains(activeElement))
}

// trap focus
const moveFocusInside = (
  element: HTMLElement,
  activeElement: HTMLElement | null
) => {
  // return or set initial focus
  activeElement ? setFocusTo(activeElement) : setFocusWithin(element)
}

const isActiveTrap = (
  activeTraps: React.ContextType<typeof FocusContext>["activeTraps"],
  id: string
) => id === last(Array.from(activeTraps.values()))

const FocusTrap = ({
  autoFocus,
  children,
  disabled: userDisabled,
  from: Component,
  restoreFocus: userRestoreFocus
}: Props) => {
  const [restoreFocus] = React.useState<boolean | FocusOptions>(
    userRestoreFocus
  )
  const internalId = useInternalId("focustrap")
  const { activeTraps, setActiveTraps } = React.useContext(FocusContext)
  const [disabled, setDisabled] = React.useState(userDisabled)
  const trapRef = React.useRef<HTMLElement>(null)
  const guardStartRef = React.useRef<HTMLDivElement>(null)
  const guardEndRef = React.useRef<HTMLDivElement>(null)
  const elementsRef = React.useRef<HTMLElement[] | null>(null)
  const lastActiveElementRef = React.useRef<HTMLElement | null>(null)

  const containFocus = React.useCallback(
    (target: HTMLElement | HTMLDocument | null) => {
      const trap = trapRef.current
      const elements = elementsRef.current
      if (
        disabled ||
        !trap ||
        !elements ||
        !target ||
        target instanceof HTMLDocument
      )
        return false
      const doc = getDocument(trap)
      if (hasFocus(elements, target)) {
        lastActiveElementRef.current = target
        return true
      }
      moveFocusInside(trap, lastActiveElementRef.current)
      return true
    },
    [disabled]
  )

  // loop focus
  const handleGuardStartOnFocusCapture = React.useCallback<
    React.FocusEventHandler
  >(
    event => {
      if (disabled || !trapRef.current) return
      focusOnEnd(trapRef.current)
      // prevent scroll jump
      event.stopPropagation()
      event.preventDefault()
    },
    [disabled]
  )

  const handleGuardEndOnFocusCapture = React.useCallback<
    React.FocusEventHandler
  >(
    event => {
      if (disabled || !trapRef.current) return
      focusOnStart(trapRef.current)
      // prevent scroll jump
      event.stopPropagation()
      event.preventDefault()
    },
    [disabled]
  )

  const handleTrapOnFocus = (event: React.FocusEvent) => {
    // detected portaled content
    if (!event.currentTarget.contains(event.target)) {
      // portaled content
      return
    }
    // set last active element
    lastActiveElementRef.current = event.target as HTMLElement
  }

  const handleTrapOnBlur = React.useCallback<React.FocusEventHandler>(
    event => {
      const relatedTarget = event.relatedTarget as HTMLElement | null
      if (containFocus(relatedTarget)) {
        // prevent scroll jump
        event.stopPropagation()
        event.preventDefault()
      }
    },
    [containFocus]
  )

  // hide outside content from screen readers
  usePerceptionTrap({ ref: trapRef, disabled })

  // add / remove trap from context
  React.useEffect(() => {
    const trap = trapRef.current
    // capture original active element
    const originalActiveElement = getDocument(trap)
      .activeElement as HTMLElement | null

    setActiveTraps(traps => {
      if (traps.has(internalId)) return traps
      const ts = new Map(traps)
      ts.set(internalId, internalId)
      return ts
    })

    return () => {
      // remove this trap from context
      setActiveTraps(traps => {
        if (!traps.has(internalId)) return traps
        const ts = new Map(traps)
        ts.delete(internalId)
        return ts
      })

      // restore focus
      if (restoreFocus && originalActiveElement) {
        const preventScroll =
          (restoreFocus as FocusOptions)?.preventScroll || false
        originalActiveElement.focus({
          preventScroll
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalId, restoreFocus])

  // disable trap if user defined or if this trap isn't the most recent trap
  // added to context
  React.useEffect(() => {
    const isActive = isActiveTrap(activeTraps, internalId)
    const isDisabled = userDisabled || !isActive
    setDisabled(isDisabled)
  }, [activeTraps, internalId, userDisabled])

  // disabled / enabled
  React.useEffect(() => {
    const trap = trapRef.current
    if (!trap || disabled) return

    const doc = getDocument(trap)
    const activeElement = doc.activeElement as HTMLElement | null

    // set elements on mount
    const elements = [trap, guardStartRef.current, guardEndRef.current].filter(
      Boolean
    ) as HTMLElement[]
    elementsRef.current = elements

    // init focus
    autoFocus && containFocus(activeElement)

    const handleDocOnFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null
      if (hasFocus(elements, target)) {
        event.stopPropagation()
        event.preventDefault()
        return
      }
      setTimeout(() => {
        moveFocusInside(trap, lastActiveElementRef.current)
      }, 0)
    }

    // NOTE: these listeners will fire after our trap specific focus handlers
    doc.addEventListener("focusin", handleDocOnFocus, true)

    return () => {
      doc.removeEventListener("focusin", handleDocOnFocus, true)
    }
  }, [autoFocus, containFocus, disabled, restoreFocus])

  return (
    <>
      <FocusGuard
        onFocusCapture={handleGuardStartOnFocusCapture}
        ref={guardStartRef}
        tabIndex={disabled ? undefined : 0}
      />
      {/**
       * Not cloning children to add functionality since this implementation
       * requires a ref and not all children take one.
       */}
      <Component
        onFocus={handleTrapOnFocus}
        onBlur={handleTrapOnBlur}
        ref={trapRef}
      >
        {children}
      </Component>
      <FocusGuard
        onFocusCapture={handleGuardEndOnFocusCapture}
        ref={guardEndRef}
        tabIndex={disabled ? undefined : 0}
      />
    </>
  )
}
FocusTrap.defaultProps = defaultProps

export default FocusTrap
